import { useEffect, useRef, useState, useCallback } from "react";
import {
  type UseCameraStreamOptions,
  type UseCameraStreamReturn,
} from "../models/cameraStream";

const FILTERED_DEVICE_LABELS = ["”的相机", "”的麦克风"];

const DEFAULT_CONSTRAINTS: UseCameraStreamOptions = {
  video: {
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    aspectRatio: { ideal: 16 / 9 },
    frameRate: { ideal: 60 },
  },
  audio: true,
};

const STRICT_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 1920, min: 1280 },
  height: { ideal: 1080, min: 720 },
  aspectRatio: { ideal: 16 / 9 },
  frameRate: { ideal: 60, min: 30 },
};

const RELAXED_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  aspectRatio: { ideal: 16 / 9 },
  frameRate: { ideal: 60 },
};

const FALLBACK_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  aspectRatio: { ideal: 16 / 9 },
  frameRate: { ideal: 30 },
};

const EXACT_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { exact: 1920 },
  height: { exact: 1080 },
  aspectRatio: { exact: 16 / 9 },
  frameRate: { exact: 60 },
};

function isConstraintObject(
  constraint: MediaTrackConstraints | boolean | undefined,
): constraint is MediaTrackConstraints {
  return typeof constraint === "object" && constraint !== null;
}

function scoreAudioMatch(
  videoDevice: MediaDeviceInfo | undefined,
  audioDevice: MediaDeviceInfo,
): number {
  if (!videoDevice) return 0;

  let score = 0;

  if (
    videoDevice.groupId &&
    audioDevice.groupId &&
    videoDevice.groupId === audioDevice.groupId
  ) {
    score += 100;
  }

  return score;
}

export function useCameraStream(
  constraints: UseCameraStreamOptions = DEFAULT_CONSTRAINTS,
): UseCameraStreamReturn {
  const isMediaSupported =
    typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const unsupportedMediaError = isMediaSupported
    ? null
    : new Error("当前环境不支持摄像头（需要 HTTPS 或 localhost）");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoSourceStreamRef = useRef<MediaStream | null>(null);
  const audioSourceStreamRef = useRef<MediaStream | null>(null);
  const videoDevicesRef = useRef<MediaDeviceInfo[]>([]);
  const audioDevicesRef = useRef<MediaDeviceInfo[]>([]);
  const audioDeviceAliasRef = useRef<Record<string, string>>({});
  const currentDeviceIdRef = useRef<string | null>(null);
  const currentAudioDeviceIdRef = useRef<string | null>(null);
  const permissionsInitializedRef = useRef(false);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null);
  const [currentAudioDeviceId, setCurrentAudioDeviceId] = useState<
    string | null
  >(null);

  const normalizeDeviceLabel = useCallback((label: string): string => {
    return label
      .replace(/^default\s*-\s*/i, "")
      .replace(/^communications\s*-\s*/i, "")
      .trim();
  }, []);

  const shouldKeepDevice = useCallback(
    (device: MediaDeviceInfo): boolean => {
      const normalizedLabel = normalizeDeviceLabel(device.label).toLowerCase();
      for (let i = 0; i < FILTERED_DEVICE_LABELS.length; i++) {
        if (normalizedLabel.endsWith(FILTERED_DEVICE_LABELS[i])) return false;
      }
      return true;
    },
    [normalizeDeviceLabel],
  );

  const dedupeAudioDevices = useCallback(
    (inputDevices: MediaDeviceInfo[]): MediaDeviceInfo[] => {
      const deduped = new Map<string, MediaDeviceInfo>();
      const aliasMap: Record<string, string> = {};

      for (const device of inputDevices) {
        const normalizedLabel = normalizeDeviceLabel(device.label);
        const dedupeKey = `${device.groupId || "nogroup"}::${normalizedLabel || device.deviceId}`;
        const existing = deduped.get(dedupeKey);

        if (!existing) {
          deduped.set(dedupeKey, device);
          aliasMap[device.deviceId] = device.deviceId;
          continue;
        }

        const existingIsDefault = /^default\s*-\s*/i.test(existing.label);
        const currentIsDefault = /^default\s*-\s*/i.test(device.label);
        const preferred =
          existingIsDefault && !currentIsDefault ? device : existing;
        const other =
          preferred.deviceId === existing.deviceId ? device : existing;

        deduped.set(dedupeKey, preferred);
        aliasMap[preferred.deviceId] = preferred.deviceId;
        aliasMap[other.deviceId] = preferred.deviceId;
      }

      audioDeviceAliasRef.current = aliasMap;
      return Array.from(deduped.values());
    },
    [normalizeDeviceLabel],
  );

  const normalizeAudioDeviceId = useCallback(
    (deviceId: string | null): string | null => {
      if (!deviceId) return null;
      return audioDeviceAliasRef.current[deviceId] ?? deviceId;
    },
    [],
  );

  const getTrackDeviceId = useCallback(
    (track: MediaStreamTrack | undefined): string | null => {
      const deviceId = track?.getSettings().deviceId;
      return typeof deviceId === "string" && deviceId.length > 0
        ? deviceId
        : null;
    },
    [],
  );

  const enumerateDevices = useCallback(async (): Promise<MediaDeviceInfo[]> => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = allDevices.filter(
        (d) => d.kind === "videoinput" && shouldKeepDevice(d),
      );
      const nextAudioDevices = dedupeAudioDevices(
        allDevices.filter(
          (d) => d.kind === "audioinput" && shouldKeepDevice(d),
        ),
      );
      videoDevicesRef.current = videoDevices;
      audioDevicesRef.current = nextAudioDevices;
      setDevices(videoDevices);
      setAudioDevices(nextAudioDevices);
      return videoDevices;
    } catch (err) {
      console.error("enumerateDevices error:", err);
      return [];
    }
  }, [dedupeAudioDevices, shouldKeepDevice]);

  // 修复要点：用 useCallback 的 ref 工具函数确保 video 元素挂载后立即写入 srcObject
  const attachStream = useCallback((mediaStream: MediaStream) => {
    const video = videoRef.current;
    if (video) {
      video.srcObject = mediaStream;
      // 确保 autoplay、muted 属性已设置
      video.muted = true;
      video.play().catch(() => {
        /* autoplay 政策拦截时忽略 */
      });
    }
  }, []);

  const stopTracks = useCallback((mediaStream: MediaStream | null) => {
    mediaStream?.getTracks().forEach((track) => track.stop());
  }, []);

  const clearCombinedStream = useCallback(() => {
    streamRef.current = null;
    setStream(null);
  }, []);

  const updateCombinedStream = useCallback(
    (videoStream: MediaStream | null, audioStream: MediaStream | null) => {
      const combinedStream = new MediaStream();

      videoStream
        ?.getVideoTracks()
        .forEach((track) => combinedStream.addTrack(track));
      audioStream
        ?.getAudioTracks()
        .forEach((track) => combinedStream.addTrack(track));

      streamRef.current = combinedStream;
      setStream(combinedStream);
      attachStream(combinedStream);
    },
    [attachStream],
  );

  const buildVideoConstraints = useCallback(
    (
      deviceId: string | null,
      preset: "exact" | "strict" | "relaxed" | "fallback",
    ): MediaTrackConstraints | boolean => {
      if (constraints.video === false) {
        return false;
      }

      const baseConstraints = isConstraintObject(constraints.video)
        ? { ...constraints.video }
        : {};
      const presetConstraints =
        preset === "exact"
          ? EXACT_VIDEO_CONSTRAINTS
          : preset === "strict"
            ? STRICT_VIDEO_CONSTRAINTS
            : preset === "relaxed"
              ? RELAXED_VIDEO_CONSTRAINTS
              : FALLBACK_VIDEO_CONSTRAINTS;

      const nextConstraints: MediaTrackConstraints = {
        ...baseConstraints,
        ...presetConstraints,
        ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
      };

      return Object.keys(nextConstraints).length > 0 ? nextConstraints : true;
    },
    [constraints.video],
  );

  const applyVideoTrackConstraints = useCallback(
    async (track: MediaStreamTrack): Promise<void> => {
      const attempts: MediaTrackConstraints[] = [
        EXACT_VIDEO_CONSTRAINTS,
        STRICT_VIDEO_CONSTRAINTS,
        RELAXED_VIDEO_CONSTRAINTS,
        FALLBACK_VIDEO_CONSTRAINTS,
      ];

      for (const attempt of attempts) {
        try {
          await track.applyConstraints(attempt);
          return;
        } catch {
          // 尝试下一组约束
        }
      }
    },
    [],
  );

  const requestVideoStream = useCallback(
    async (deviceId: string | null): Promise<MediaStream> => {
      const presets: Array<"exact" | "strict" | "relaxed" | "fallback"> = [
        "exact",
        "strict",
        "relaxed",
        "fallback",
      ];
      let lastError: unknown = null;

      for (const preset of presets) {
        try {
          const mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: buildVideoConstraints(deviceId, preset),
          });
          const videoTrack = mediaStream.getVideoTracks()[0];
          if (videoTrack) {
            await applyVideoTrackConstraints(videoTrack);
          }
          return mediaStream;
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError ?? new Error("无法启动摄像头视频流");
    },
    [applyVideoTrackConstraints, buildVideoConstraints],
  );

  const resolveAudioDeviceId = useCallback(
    (
      videoDeviceId: string | null,
      preferredAudioDeviceId: string | null,
    ): string | null => {
      if (constraints.audio === false) return null;

      const availableAudioDevices = audioDevicesRef.current;
      const normalizedPreferredAudioDeviceId = normalizeAudioDeviceId(
        preferredAudioDeviceId,
      );
      if (normalizedPreferredAudioDeviceId) {
        const exists = availableAudioDevices.some(
          (device) => device.deviceId === normalizedPreferredAudioDeviceId,
        );
        if (exists) return normalizedPreferredAudioDeviceId;
      }

      const selectedVideoDevice = videoDeviceId
        ? videoDevicesRef.current.find(
            (device) => device.deviceId === videoDeviceId,
          )
        : undefined;

      if (!selectedVideoDevice) {
        return availableAudioDevices.length === 1
          ? availableAudioDevices[0].deviceId
          : null;
      }

      const rankedAudioDevices = availableAudioDevices
        .map((device) => ({
          device,
          score: scoreAudioMatch(selectedVideoDevice, device),
        }))
        .sort((a, b) => b.score - a.score);

      if (rankedAudioDevices[0] && rankedAudioDevices[0].score > 0) {
        return rankedAudioDevices[0].device.deviceId;
      }

      return availableAudioDevices.length === 1
        ? availableAudioDevices[0].deviceId
        : null;
    },
    [constraints.audio, normalizeAudioDeviceId],
  );

  const buildAudioConstraints = useCallback(
    (audioDeviceId: string | null): MediaTrackConstraints | boolean => {
      if (constraints.audio === false || !audioDeviceId) {
        return false;
      }

      const baseConstraints = isConstraintObject(constraints.audio)
        ? { ...constraints.audio }
        : {};

      const nextConstraints: MediaTrackConstraints = {
        ...baseConstraints,
        deviceId: { exact: audioDeviceId },
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: { ideal: 2 },
      };

      return nextConstraints;
    },
    [constraints.audio],
  );

  const requestAudioStream = useCallback(
    async (audioDeviceId: string | null): Promise<MediaStream | null> => {
      if (constraints.audio === false || !audioDeviceId) {
        return null;
      }

      try {
        return await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: buildAudioConstraints(audioDeviceId),
        });
      } catch (error) {
        console.warn("audio getUserMedia failed:", error);
        return null;
      }
    },
    [buildAudioConstraints, constraints.audio],
  );

  const ensureInitialPermissions = useCallback(async (): Promise<void> => {
    if (permissionsInitializedRef.current || !isMediaSupported) {
      return;
    }

    const permissionStream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    stopTracks(permissionStream);
    permissionsInitializedRef.current = true;
  }, [isMediaSupported, stopTracks]);

  const startCamera = useCallback(
    async (
      deviceId: string | null = null,
      audioDeviceId: string | null = null,
    ): Promise<void> => {
      stopTracks(videoSourceStreamRef.current);
      stopTracks(audioSourceStreamRef.current);
      videoSourceStreamRef.current = null;
      audioSourceStreamRef.current = null;
      clearCombinedStream();

      setIsLoading(true);
      setError(null);

      const nextVideoDeviceId = deviceId ?? currentDeviceIdRef.current;
      const nextAudioDeviceId = resolveAudioDeviceId(
        nextVideoDeviceId,
        audioDeviceId,
      );

      try {
        const videoStream = await requestVideoStream(nextVideoDeviceId);
        const actualVideoDeviceId =
          getTrackDeviceId(videoStream.getVideoTracks()[0]) ??
          nextVideoDeviceId;
        const resolvedAudioDeviceId = resolveAudioDeviceId(
          actualVideoDeviceId,
          nextAudioDeviceId,
        );
        const audioStream = await requestAudioStream(resolvedAudioDeviceId);
        const actualAudioDeviceId = normalizeAudioDeviceId(
          getTrackDeviceId(audioStream?.getAudioTracks()[0]) ??
            resolvedAudioDeviceId,
        );

        videoSourceStreamRef.current = videoStream;
        audioSourceStreamRef.current = audioStream;
        currentDeviceIdRef.current = actualVideoDeviceId;
        currentAudioDeviceIdRef.current =
          audioStream && audioStream.getAudioTracks().length > 0
            ? actualAudioDeviceId
            : null;
        setCurrentDeviceId(actualVideoDeviceId);
        setCurrentAudioDeviceId(currentAudioDeviceIdRef.current);
        updateCombinedStream(videoStream, audioStream);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      clearCombinedStream,
      getTrackDeviceId,
      normalizeAudioDeviceId,
      requestAudioStream,
      requestVideoStream,
      resolveAudioDeviceId,
      stopTracks,
      updateCombinedStream,
    ],
  );

  const switchCamera = useCallback(
    async (deviceId: string): Promise<void> => {
      await startCamera(deviceId, null);
    },
    [startCamera],
  );

  const switchAudio = useCallback(
    async (deviceId: string | null): Promise<void> => {
      if (!videoSourceStreamRef.current) {
        await startCamera(currentDeviceIdRef.current, deviceId);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        stopTracks(audioSourceStreamRef.current);
        audioSourceStreamRef.current = null;

        const nextAudioDeviceId = resolveAudioDeviceId(
          currentDeviceIdRef.current,
          deviceId,
        );
        const audioStream = await requestAudioStream(nextAudioDeviceId);
        const actualAudioDeviceId = normalizeAudioDeviceId(
          getTrackDeviceId(audioStream?.getAudioTracks()[0]) ??
            nextAudioDeviceId,
        );
        audioSourceStreamRef.current = audioStream;
        currentAudioDeviceIdRef.current =
          audioStream && audioStream.getAudioTracks().length > 0
            ? actualAudioDeviceId
            : null;
        setCurrentAudioDeviceId(currentAudioDeviceIdRef.current);
        updateCombinedStream(videoSourceStreamRef.current, audioStream);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      getTrackDeviceId,
      normalizeAudioDeviceId,
      requestAudioStream,
      resolveAudioDeviceId,
      startCamera,
      stopTracks,
      updateCombinedStream,
    ],
  );

  const stopCamera = useCallback((): void => {
    stopTracks(videoSourceStreamRef.current);
    stopTracks(audioSourceStreamRef.current);
    videoSourceStreamRef.current = null;
    audioSourceStreamRef.current = null;
    clearCombinedStream();
    if (videoRef.current) videoRef.current.srcObject = null;
  }, [clearCombinedStream, stopTracks]);

  // 修复要点：监听 stream 变化，确保 video 元素挂载后也能正确绑定
  useEffect(() => {
    if (stream && videoRef.current && videoRef.current.srcObject !== stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, [stream]);

  useEffect(() => {
    if (!isMediaSupported) {
      return;
    }
    const initializeMedia = async () => {
      await ensureInitialPermissions();
      await enumerateDevices();
      await startCamera();
    };

    void initializeMedia();
    return () => {
      stopCamera();
    };
  }, [
    ensureInitialPermissions,
    enumerateDevices,
    isMediaSupported,
    startCamera,
    stopCamera,
  ]);

  return {
    videoRef,
    stream,
    error: error ?? unsupportedMediaError,
    isLoading,
    devices,
    audioDevices,
    currentDeviceId,
    currentAudioDeviceId,
    switchCamera,
    switchAudio,
    stopCamera,
    startCamera,
  };
}
